import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { BarChart3, Users, Package, DollarSign, Gift, AlertCircle, RefreshCw, Tag, Shield, Trash2, CheckCircle, XCircle, Copy, Ticket, Plus, Pencil, Upload, Video, Image as ImageIcon, X as XIcon, MessageCircle, Sparkles, Share2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import Button from "../components/ui/Button";
import Badge from "../components/ui/Badge";
import UserManagementTab from "../components/admin/UserManagementTab";
import RedeemCodesTab from "../components/admin/RedeemCodesTab";
import ProductEditor from "../components/admin/ProductEditor";
import SupportTicketsTab from "../components/admin/SupportTicketsTab";
import TrialStatsTab from "../components/admin/TrialStatsTab";
import AdminSocials from "./AdminSocials";

const AdminDashboard = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [trialClaims, setTrialClaims] = useState([]);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [loadingData, setLoadingData] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [pricingForm, setPricingForm] = useState({
    base_price: 0,
    discount_percentage: 0,
    discount_amount: 0,
    discount_active: false,
  });
  // Full product editor modal state — null = closed, {} = new product, {product_id: ...} = edit
  const [productEditorState, setProductEditorState] = useState(null);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) {
      navigate("/");
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user && user.role === "admin") {
      fetchAdminData();
    }
  }, [user]);

  const fetchAdminData = async () => {
    try {
      // Fetch stats
      const statsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/stats`, {
        credentials: "include",
      });
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      // Fetch recent orders
      const ordersRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/orders?limit=20`, {
        credentials: "include",
      });
      if (ordersRes.ok) {
        const ordersData = await ordersRes.json();
        setOrders(ordersData);
      }

      // Fetch trial claims
      const claimsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/trial-claims`, {
        credentials: "include",
      });
      if (claimsRes.ok) {
        const claimsData = await claimsRes.json();
        setTrialClaims(claimsData);
      }

      // Fetch users
      const usersRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users?limit=30`, {
        credentials: "include",
      });
      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setUsers(usersData);
      }

      // Fetch products
      const productsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/products`, {
        credentials: "include",
      });
      if (productsRes.ok) {
        const productsData = await productsRes.json();
        setProducts(productsData);
      }
    } catch (error) {
      console.error("Failed to fetch admin data:", error);
    } finally {
      setLoadingData(false);
    }
  };

  const handleResetTrial = async (orderId) => {
    if (!window.confirm("Sei sicuro di voler resettare questo trial claim?")) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/orders/${orderId}/manual-trial-reset`,
        {
          method: "POST",
          credentials: "include",
        }
      );

      if (response.ok) {
        alert("✅ Trial reset con successo!");
        fetchAdminData();
      } else {
        alert("❌ Errore nel reset del trial");
      }
    } catch (error) {
      console.error("Reset failed:", error);
      alert("❌ Errore di rete");
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm(
      `Cancellare definitivamente l'ordine ${orderId.substring(0, 12)}…?\n\nQuesta azione revocherà anche eventuali licenze generate dall'ordine.`,
    )) return;
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/orders/${orderId}`,
        { method: "DELETE", credentials: "include" },
      );
      const data = await resp.json();
      if (resp.ok) {
        alert(`✅ Ordine cancellato (${data.licenses_revoked} licenze revocate)`);
        fetchAdminData();
      } else {
        alert(`❌ ${data?.detail || "Errore nella cancellazione"}`);
      }
    } catch {
      alert("❌ Errore di rete");
    }
  };

  const handleResetRevenue = async () => {
    if (!window.confirm(
      "Azzerare il contatore Revenue Totale?\n\nGli ordini restano nel database ma il totale mostrato ripartirà da €0 (checkpoint applicato adesso).",
    )) return;
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/revenue/reset`,
        { method: "POST", credentials: "include" },
      );
      if (resp.ok) {
        alert("✅ Revenue azzerato.");
        fetchAdminData();
      } else {
        alert("❌ Errore nell'azzeramento");
      }
    } catch {
      alert("❌ Errore di rete");
    }
  };

  const handleEditPricing = (product) => {
    setEditingProduct(product.product_id);
    setPricingForm({
      base_price: product.base_price || 0,
      discount_percentage: product.discount_percentage || 0,
      discount_amount: product.discount_amount || 0,
      discount_active: product.discount_active || false,
    });
  };

  const handleUpdatePricing = async (productId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/products/${productId}/pricing`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(pricingForm),
        }
      );

      if (response.ok) {
        alert("✅ Prezzo aggiornato con successo!");
        setEditingProduct(null);
        fetchAdminData();
      } else {
        const error = await response.json();
        alert(`❌ Errore: ${error.detail}`);
      }
    } catch (error) {
      console.error("Update pricing failed:", error);
      alert("❌ Errore di rete");
    }
  };

  const handleDeleteProduct = async (product) => {
    const active = !!product.active;
    const msg = active
      ? `Disattivare "${product.name}"? Non sarà più visibile nello Store.`
      : `Eliminare definitivamente "${product.name}"? Questa azione è irreversibile.`;
    if (!window.confirm(msg)) return;
    try {
      const qs = active ? "" : "?hard=true";
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/products/${product.product_id}${qs}`,
        { method: "DELETE", credentials: "include" }
      );
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        alert(`❌ Errore: ${err.detail || resp.status}`);
        return;
      }
      alert("✅ Operazione completata");
      fetchAdminData();
    } catch (e) {
      alert("❌ Errore di rete: " + e.message);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {    if (!window.confirm(`Sei sicuro di voler cambiare il ruolo a ${newRole}?`)) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}/role?role=${newRole}`,
        {
          method: "PATCH",
          credentials: "include",
        }
      );

      if (response.ok) {
        alert(`✅ Ruolo aggiornato a ${newRole}!`);
        fetchAdminData();
      } else {
        const error = await response.json();
        alert(`❌ Errore: ${error.detail}`);
      }
    } catch (error) {
      console.error("Update role failed:", error);
      alert("❌ Errore di rete");
    }
  };

  if (loading || loadingData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-16 h-16 border-4 border-ruby border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!user || user.role !== "admin") return null;

  return (
    <div className="min-h-screen py-20 px-6 bg-gradient-to-b from-background to-background/95">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-2">
            <h1 className="font-display text-4xl text-ruby">Admin Dashboard</h1>
            <Button onClick={() => navigate("/dashboard")} variant="ghost">
              ← User Dashboard
            </Button>
          </div>
          <p className="text-muted-foreground">Gestione completa di Mytharion</p>
        </div>

        {/* Product Editor Modal */}
        {productEditorState !== null && (
          <ProductEditor
            product={productEditorState.product_id ? productEditorState : null}
            onClose={() => setProductEditorState(null)}
            onSaved={() => { setProductEditorState(null); fetchAdminData(); }}
          />
        )}

        {/* Stats Grid */}
        {stats && (
          <div className="grid md:grid-cols-4 gap-6 mb-12">
            <Card className="border-gold/50">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between text-sm text-muted-foreground">
                  <span className="flex items-center">
                    <DollarSign className="w-4 h-4 mr-2" />
                    Revenue Totale
                  </span>
                  <button
                    onClick={handleResetRevenue}
                    className="p-1 rounded text-ruby hover:bg-ruby/10 transition-colors"
                    title="Azzera revenue totale"
                    data-testid="btn-reset-revenue"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-display text-3xl text-gold">€{stats.total_revenue}</p>
                <p className="text-xs text-muted-foreground mt-1">{stats.paid_orders} ordini pagati</p>
                {stats.revenue_reset_at && (
                  <p className="text-[10px] text-muted-foreground/70 mt-1">
                    dal {new Date(stats.revenue_reset_at).toLocaleDateString("it-IT")}
                  </p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center text-sm text-muted-foreground">
                  <Package className="w-4 h-4 mr-2" />
                  Ordini Totali
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-display text-3xl text-foreground">{stats.total_orders}</p>
                <p className="text-xs text-muted-foreground mt-1">{stats.total_products} prodotti attivi</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center text-sm text-muted-foreground">
                  <Users className="w-4 h-4 mr-2" />
                  Utenti Registrati
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-display text-3xl text-foreground">{stats.total_users}</p>
              </CardContent>
            </Card>

            <Card className="border-purple-500/50">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center text-sm text-muted-foreground">
                  <Gift className="w-4 h-4 mr-2" />
                  Trial Claims
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-display text-3xl text-arcane">{stats.trial_claims}</p>
                <p className="text-xs text-muted-foreground mt-1">{stats.pending_trials} in attesa</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-border overflow-x-auto">
          {[
            { id: "overview", label: "Overview", icon: BarChart3 },
            { id: "orders", label: "Ordini", icon: Package },
            { id: "trials", label: "Trial Claims", icon: Gift },
            { id: "users", label: "Utenti", icon: Users },
            { id: "pricing", label: "Pricing", icon: Tag },
            { id: "roles", label: "Ruoli", icon: Shield },
            { id: "user-mgmt", label: "Gestione Utenti", icon: Users },
            { id: "codes", label: "Codici Redeem", icon: Ticket },
            { id: "trial-stats", label: "Statistiche Trial", icon: Sparkles },
            { id: "support", label: "Ticket & Messaggi", icon: MessageCircle },
            { id: "socials", label: "Social Hub", icon: Share2 },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`admin-tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-3 font-heading text-sm transition-colors border-b-2 whitespace-nowrap ${
                  activeTab === tab.id
                    ? "border-ruby text-ruby"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === "overview" && (
          <div className="grid md:grid-cols-2 gap-8">
            {/* Recent Orders */}
            <Card>
              <CardHeader>
                <CardTitle>Ordini Recenti</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {orders.slice(0, 5).map((order) => (
                    <div key={order.order_id} className="p-3 border border-border rounded-lg">
                      <div className="flex justify-between items-start mb-1">
                        <p className="font-heading text-sm">{order.product_name}</p>
                        <Badge variant={order.payment_status === "paid" ? "gold" : "ruby"}>
                          {order.payment_status}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">{order.user_email}</p>
                      <p className="text-xs text-gold mt-1">€{order.amount}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Products */}
            <Card>
              <CardHeader>
                <CardTitle>Prodotti</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {products.map((product) => (
                    <div key={product.product_id} className="p-3 border border-border rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-heading text-sm">{product.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {product.subscription ? "Abbonamento" : "Una tantum"}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-heading text-gold">€{product.final_price}</p>
                          {product.discount_active && (
                            <p className="text-xs text-ruby line-through">€{product.base_price}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === "orders" && (
          <Card>
            <CardHeader>
              <CardTitle>Tutti gli Ordini</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-border">
                    <tr className="text-left">
                      <th className="pb-3 font-heading text-xs text-muted-foreground">ORDER ID</th>
                      <th className="pb-3 font-heading text-xs text-muted-foreground">CLIENTE</th>
                      <th className="pb-3 font-heading text-xs text-muted-foreground">PRODOTTO</th>
                      <th className="pb-3 font-heading text-xs text-muted-foreground">IMPORTO</th>
                      <th className="pb-3 font-heading text-xs text-muted-foreground">STATUS</th>
                      <th className="pb-3 font-heading text-xs text-muted-foreground">TRIAL</th>
                      <th className="pb-3 font-heading text-xs text-muted-foreground text-right">AZIONI</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order.order_id} className="border-b border-border/50">
                        <td className="py-3 font-mono text-xs">{order.order_id.substring(0, 12)}...</td>
                        <td className="py-3 text-sm">
                          <p>{order.user_name}</p>
                          <p className="text-xs text-muted-foreground">{order.user_email}</p>
                        </td>
                        <td className="py-3 text-sm">{order.product_name}</td>
                        <td className="py-3 text-gold font-heading">€{order.amount}</td>
                        <td className="py-3">
                          <Badge variant={order.payment_status === "paid" ? "gold" : "ruby"}>
                            {order.payment_status}
                          </Badge>
                        </td>
                        <td className="py-3">
                          {order.premium_trial_claimed ? (
                            <span className="text-xs text-arcane">✓ Claimed</span>
                          ) : order.payment_status === "paid" ? (
                            <span className="text-xs text-muted-foreground">Disponibile</span>
                          ) : (
                            <span className="text-xs text-muted-foreground/50">N/A</span>
                          )}
                        </td>
                        <td className="py-3 text-right">
                          <button
                            onClick={() => handleDeleteOrder(order.order_id)}
                            className="p-1.5 rounded text-ruby hover:bg-ruby/10 transition-colors"
                            title="Cancella ordine"
                            data-testid={`btn-delete-order-${order.order_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === "trials" && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Gift className="w-5 h-5 mr-2 text-arcane" />
                Premium Trial Claims
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {trialClaims.map((claim) => (
                  <div key={claim.order_id} className="p-4 border border-purple-500/30 bg-purple-900/10 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <p className="font-heading text-foreground">{claim.user_name}</p>
                          <Badge variant="arcane">CLAIMED</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-1">{claim.user_email}</p>
                        <div className="grid grid-cols-2 gap-4 mt-3 text-xs">
                          <div>
                            <p className="text-muted-foreground">Prodotto</p>
                            <p className="text-foreground">{claim.product_name}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Trial Code</p>
                            <p className="text-gold font-mono">{claim.premium_trial_code}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Data Claim</p>
                            <p className="text-foreground">
                              {new Date(claim.premium_trial_claimed_at).toLocaleDateString("it-IT")}
                            </p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Order ID</p>
                            <p className="text-foreground font-mono text-xs">{claim.order_id.substring(0, 12)}...</p>
                          </div>
                        </div>
                      </div>
                      <Button
                        onClick={() => handleResetTrial(claim.order_id)}
                        variant="destructive"
                        size="sm"
                      >
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Reset
                      </Button>
                    </div>
                  </div>
                ))}
                {trialClaims.length === 0 && (
                  <div className="text-center py-12">
                    <AlertCircle className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                    <p className="text-muted-foreground">Nessun trial claim ancora</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === "users" && (
          <Card>
            <CardHeader>
              <CardTitle>Utenti Registrati</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-border">
                    <tr className="text-left">
                      <th className="pb-3 font-heading text-xs text-muted-foreground">NOME</th>
                      <th className="pb-3 font-heading text-xs text-muted-foreground">EMAIL</th>
                      <th className="pb-3 font-heading text-xs text-muted-foreground">ROLE</th>
                      <th className="pb-3 font-heading text-xs text-muted-foreground">ORDINI</th>
                      <th className="pb-3 font-heading text-xs text-muted-foreground">REGISTRATO</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((userData) => (
                      <tr key={userData.user_id} className="border-b border-border/50">
                        <td className="py-3 text-sm">{userData.name}</td>
                        <td className="py-3 text-sm text-muted-foreground">{userData.email}</td>
                        <td className="py-3">
                          <Badge variant={userData.role === "admin" ? "ruby" : "gold"}>
                            {userData.role}
                          </Badge>
                        </td>
                        <td className="py-3 text-sm">{userData.order_count || 0}</td>
                        <td className="py-3 text-xs text-muted-foreground">
                          {new Date(userData.created_at).toLocaleDateString("it-IT")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === "pricing" && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center">
                  <Tag className="w-5 h-5 mr-2 text-gold" />
                  Gestione Prezzi e Sconti
                </CardTitle>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setProductEditorState({})}
                  data-testid="admin-new-product-btn"
                >
                  <Plus className="w-4 h-4 mr-2" /> Nuovo Prodotto
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {products.map((product) => (
                  <div
                    key={product.product_id}
                    className="p-6 border border-gold/30 bg-gold/5 rounded-lg"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-heading text-lg text-foreground mb-1">{product.name}</h3>
                        <p className="text-sm text-muted-foreground">
                          {product.subscription ? "Abbonamento mensile" : "Acquisto una tantum"}
                        </p>
                      </div>
                      <Badge variant={product.discount_active ? "ruby" : "gold"}>
                        {product.discount_active ? "Sconto Attivo" : "Prezzo Base"}
                      </Badge>
                    </div>

                    {editingProduct === product.product_id ? (
                      <div className="space-y-4 mt-4 p-4 bg-background/50 rounded-lg">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-heading text-muted-foreground mb-2">
                              Prezzo Base (€)
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              value={pricingForm.base_price}
                              onChange={(e) =>
                                setPricingForm({ ...pricingForm, base_price: parseFloat(e.target.value) })
                              }
                              className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:border-gold focus:outline-none"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-heading text-muted-foreground mb-2">
                              Sconto Percentuale (%)
                            </label>
                            <input
                              type="number"
                              step="1"
                              value={pricingForm.discount_percentage}
                              onChange={(e) =>
                                setPricingForm({ ...pricingForm, discount_percentage: parseFloat(e.target.value) })
                              }
                              className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:border-gold focus:outline-none"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-heading text-muted-foreground mb-2">
                              Sconto Fisso (€)
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              value={pricingForm.discount_amount}
                              onChange={(e) =>
                                setPricingForm({ ...pricingForm, discount_amount: parseFloat(e.target.value) })
                              }
                              className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:border-gold focus:outline-none"
                            />
                          </div>
                          <div className="flex items-center">
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={pricingForm.discount_active}
                                onChange={(e) =>
                                  setPricingForm({ ...pricingForm, discount_active: e.target.checked })
                                }
                                className="w-5 h-5 text-ruby bg-background border-border rounded focus:ring-ruby focus:ring-2"
                              />
                              <span className="ml-2 text-sm font-heading text-foreground">Attiva Sconto</span>
                            </label>
                          </div>
                        </div>
                        <div className="flex gap-3 mt-4">
                          <Button onClick={() => handleUpdatePricing(product.product_id)} variant="primary">
                            💾 Salva Modifiche
                          </Button>
                          <Button onClick={() => setEditingProduct(null)} variant="ghost">
                            Annulla
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between mt-4 gap-4 flex-wrap">
                        <div className="flex gap-6 flex-wrap">
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Prezzo Base</p>
                            <p className={`font-heading text-xl ${product.discount_active ? "text-ruby line-through" : "text-foreground"}`} data-testid={`price-base-${product.product_id}`}>
                              €{Number(product.base_price || 0).toFixed(2)}
                            </p>
                          </div>
                          {product.discount_active && (
                            <>
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">Sconto</p>
                                <p className="font-heading text-ruby">
                                  {product.discount_percentage > 0 && `${product.discount_percentage}%`}
                                  {product.discount_amount > 0 && ` -€${product.discount_amount}`}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">Prezzo Finale</p>
                                <p className="font-heading text-2xl text-gold" data-testid={`price-final-${product.product_id}`}>€{Number(product.final_price || 0).toFixed(2)}</p>
                              </div>
                            </>
                          )}
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <Button onClick={() => handleEditPricing(product)} variant="secondary" size="sm" data-testid={`admin-edit-pricing-${product.product_id}`}>
                            <Tag className="w-4 h-4 mr-1" /> Solo Prezzo
                          </Button>
                          <Button
                            onClick={() => setProductEditorState(product)}
                            variant="primary"
                            size="sm"
                            data-testid={`admin-edit-full-${product.product_id}`}
                          >
                            <Pencil className="w-4 h-4 mr-1" /> Modifica Completo
                          </Button>
                          <Button
                            onClick={() => handleDeleteProduct(product)}
                            variant="ghost"
                            size="sm"
                            data-testid={`admin-delete-${product.product_id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-1 text-ruby" /> {product.active ? "Disattiva" : "Elimina"}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === "roles" && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Shield className="w-5 h-5 mr-2 text-ruby" />
                Gestione Ruoli Utenti
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {users.map((userData) => (
                  <div
                    key={userData.user_id}
                    className="p-4 border border-border rounded-lg flex items-center justify-between"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <p className="font-heading text-foreground">{userData.name}</p>
                        <Badge variant={userData.role === "admin" ? "ruby" : "gold"}>
                          {userData.role}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{userData.email}</p>
                      <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                        <span>{userData.order_count || 0} ordini</span>
                        <span>Registrato: {new Date(userData.created_at).toLocaleDateString("it-IT")}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {userData.role !== "admin" && (
                        <Button
                          onClick={() => handleUpdateRole(userData.user_id, "admin")}
                          variant="ruby"
                          size="sm"
                        >
                          ⬆️ Promuovi Admin
                        </Button>
                      )}
                      {userData.role === "admin" && (
                        <Button
                          onClick={() => handleUpdateRole(userData.user_id, "user")}
                          variant="ghost"
                          size="sm"
                        >
                          ⬇️ Retrocedi a User
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* User Management Tab */}
        {activeTab === "user-mgmt" && <UserManagementTab />}
        
        {/* Redeem Codes Tab */}
        {activeTab === "codes" && <RedeemCodesTab />}

        {/* Trial Stats Tab */}
        {activeTab === "trial-stats" && <TrialStatsTab />}

        {/* Support Tickets Tab */}
        {activeTab === "support" && <SupportTicketsTab />}

        {activeTab === "socials" && (
          <div data-testid="admin-social-hub-embed" className="-mx-6 -mt-8">
            <AdminSocials />
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
